import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Heart, Calendar, FileText, Apple, Video, Brain, Utensils, Globe, Shield, Stethoscope, Pill, Sparkles, Car, ArrowRight, ShoppingCart } from 'lucide-react';
import HeroDemo from '../components/HeroDemo';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { API } from '../App';
import { toast, Toaster } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/config';
import AndroidInstallBanner from '../components/AndroidInstallBanner';
import secureStorage from '../utils/secureStorage';

const LandingPage = ({ setUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'patient'
  });

  const languages = [
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'hi', name: 'हिंदी', flag: '🇮🇳' },
    { code: 'te', name: 'తెలుగు', flag: '🇮🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' }
  ];

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Auth check is handled by App.js - no duplicate check needed here

  const validateForm = (isLogin) => {
    const newErrors = {};
    const email = formData.email?.trim();
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    const password = formData.password;
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (!isLogin) {
      const fullName = formData.full_name?.trim();
      if (!fullName) {
        newErrors.full_name = 'Full name is required';
      } else if (fullName.length < 2) {
        newErrors.full_name = 'Full name must be at least 2 characters';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAuth = async (isLogin) => {
    if (!validateForm(isLogin)) {
      toast.error('Please fix the validation errors');
      return;
    }

    setLoading(true);
    setErrors({});
    
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin
        ? { email: formData.email.trim().toLowerCase(), password: formData.password }
        : {
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
            full_name: formData.full_name.trim(),
            role: formData.role
          };

      const response = await axios.post(`${API}${endpoint}`, payload);
      secureStorage.setToken(response.data.access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      if (!error.response) {
        toast.error('Network error. Please check your connection.');
        return;
      }

      const status = error.response?.status;
      const errorDetail = error.response?.data?.detail || '';
      
      if (status === 401) {
        toast.error('Invalid email or password');
        setErrors({ email: 'Invalid credentials', password: 'Invalid credentials' });
      } else if (status === 400) {
        if (errorDetail.toLowerCase().includes('email already registered')) {
          toast.error('This email is already registered. Please login.');
          setErrors({ email: 'Email already registered' });
        } else {
          toast.error(errorDetail || 'Invalid input');
        }
      } else {
        toast.error('Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearErrors = () => setErrors({});

  const features = [
    { icon: Brain, title: 'AI Health Assistant', desc: 'Get instant health advice powered by advanced AI' },
    { icon: Stethoscope, title: 'Symptom Checker', desc: 'Analyze symptoms and get preliminary assessments' },
    { icon: Video, title: 'Video Consultations', desc: 'Connect with doctors via secure video calls' },
    { icon: Pill, title: 'Medicine Scanner', desc: 'Identify pills and check drug interactions' },
    { icon: Shield, title: 'HIPAA Compliant', desc: 'Your health data is secure and protected' },
    { icon: Activity, title: 'Health Tracking', desc: 'Monitor vitals, diet, and fitness goals' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center shadow-lg">
              <Heart className="w-6 h-6 text-white" fill="white" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
              HealthAI
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/food-compare"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-orange-700 bg-orange-50 hover:bg-orange-100 border border-orange-200 transition"
              data-testid="nav-food-compare"
            >
              <Utensils className="w-3 h-3" />
              FoodCompare
            </a>
            <a
              href="/ride-compare"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-violet-700 bg-violet-50 hover:bg-violet-100 border border-violet-200 transition"
              data-testid="nav-ride-compare"
            >
              <Car className="w-3 h-3" />
              RideCompare
            </a>
            <a
              href="/grocery-compare"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 border border-amber-200 transition"
              data-testid="nav-grocery-compare"
            >
              <ShoppingCart className="w-3 h-3" />
              GroceryCompare
            </a>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" data-testid="language-switcher">
                  <Globe className="h-4 w-4" />
                  <span className="text-xs">{languages.find(l => l.code === i18n.language)?.flag || '🌐'}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={i18n.language === lang.code ? 'bg-blue-50 text-blue-600' : ''}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content - Hero + Auth Form */}
      <div className="container mx-auto px-4 py-8 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
          {/* Left Side - Hero Content */}
          <div className="space-y-6 lg:space-y-8" data-testid="hero-section">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="bg-gradient-to-r from-teal-700 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
                AI-Powered
              </span>
              <br />
              <span className="text-gray-800">Healthcare</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-lg">
              Experience the future of healthcare with AI symptom analysis, telemedicine, 
              smartwatch integration, and comprehensive health management—all in one platform.
            </p>
            
            {/* Feature Highlights */}
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {features.slice(0, 4).map((feature, idx) => (
                <div key={`item-${idx}`} className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 bg-white/60 rounded-lg border border-teal-100">
                  <feature.icon className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-gray-800">{feature.title}</p>
                    <p className="text-xs text-gray-500 hidden sm:block">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-3 sm:gap-4 pt-4">
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Shield className="w-4 h-4 text-green-600" />
                <span>HIPAA Compliant</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Heart className="w-4 h-4 text-red-500" />
                <span>1.2K+ Users</span>
              </div>
              <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                <Activity className="w-4 h-4 text-blue-600" />
                <span>24/7 AI Support</span>
              </div>
            </div>
          </div>

          {/* Right Side - Auth Form */}
          <div className="w-full max-w-md mx-auto lg:mx-0">
            <Card className="p-6 sm:p-8 shadow-xl border-2 border-teal-100 bg-white" data-testid="auth-card">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold text-gray-800">Welcome to HealthAI</h2>
                <p className="text-sm text-gray-500 mt-1">Sign in to manage your health</p>
              </div>

              <Tabs defaultValue="login" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>
                
                {/* Login Tab */}
                <TabsContent value="login" className="space-y-4">
                  {errors.general && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">{errors.general}</p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => { setFormData({ ...formData, email: e.target.value }); clearErrors(); }}
                      onKeyPress={(e) => e.key === 'Enter' && handleAuth(true)}
                      className={errors.email ? 'border-red-500' : ''}
                      disabled={loading}
                      data-testid="login-email-input"
                    />
                    {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => { setFormData({ ...formData, password: e.target.value }); clearErrors(); }}
                      onKeyPress={(e) => e.key === 'Enter' && handleAuth(true)}
                      className={errors.password ? 'border-red-500' : ''}
                      disabled={loading}
                      data-testid="login-password-input"
                    />
                    {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
                  </div>

                  {/* Demo Credentials */}
                  <div className="bg-gray-50 p-3 rounded-lg border text-xs">
                    <p className="font-semibold text-gray-700 mb-1">Demo Credentials:</p>
                    <p className="text-gray-500"><strong>Patient:</strong> patient@test.com / password123</p>
                    <p className="text-gray-500"><strong>Doctor:</strong> doctor@test.com / password123</p>
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                    onClick={() => handleAuth(true)}
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Signing in...
                      </span>
                    ) : 'Sign In'}
                  </Button>
                </TabsContent>
                
                {/* Register Tab */}
                <TabsContent value="register" className="space-y-4">
                  {errors.general && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm text-red-700">{errors.general}</p>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={(e) => { setFormData({ ...formData, full_name: e.target.value }); clearErrors(); }}
                      className={errors.full_name ? 'border-red-500' : ''}
                      disabled={loading}
                      data-testid="register-name-input"
                    />
                    {errors.full_name && <p className="text-xs text-red-600">{errors.full_name}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => { setFormData({ ...formData, email: e.target.value }); clearErrors(); }}
                      className={errors.email ? 'border-red-500' : ''}
                      disabled={loading}
                      data-testid="register-email-input"
                    />
                    {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="At least 6 characters"
                      value={formData.password}
                      onChange={(e) => { setFormData({ ...formData, password: e.target.value }); clearErrors(); }}
                      className={errors.password ? 'border-red-500' : ''}
                      disabled={loading}
                      data-testid="register-password-input"
                    />
                    {errors.password && <p className="text-xs text-red-600">{errors.password}</p>}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role">I am a</Label>
                    <select
                      id="role"
                      className="w-full px-3 py-2 border rounded-md border-gray-300 focus:border-teal-500 focus:ring-teal-500"
                      value={formData.role}
                      onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                      disabled={loading}
                      data-testid="register-role-select"
                    >
                      <option value="patient">Patient</option>
                      <option value="doctor">Doctor</option>
                    </select>
                  </div>
                  
                  <Button 
                    className="w-full bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700"
                    onClick={() => handleAuth(false)}
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Creating account...
                      </span>
                    ) : 'Create Account'}
                  </Button>
                </TabsContent>
              </Tabs>
            </Card>
          </div>
        </div>
      </div>

      {/* Free AI Tools — Public, No Login Required */}
      <div className="bg-gradient-to-br from-slate-50 via-white to-violet-50 py-12 sm:py-16" data-testid="free-tools-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Free AI Tools • No signup required</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">
              Compare. Save. Live smarter.
            </h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Get the best deals on food delivery and rides with our AI-powered price comparison tools.
            </p>
          </div>

          {/* Auto-playing animated demo (cycles through Food/Ride/Grocery) */}
          <div className="mb-10 sm:mb-12">
            <HeroDemo />
          </div>

          <div className="grid md:grid-cols-3 gap-5 sm:gap-6 max-w-6xl mx-auto">
            {/* Food Compare AI Card */}
            <button
              onClick={() => navigate('/food-compare')}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 via-red-500 to-pink-600 p-6 sm:p-8 text-left text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 duration-300"
              data-testid="food-compare-card"
            >
              <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-40 h-40 bg-yellow-300/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Utensils className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-white/15 px-2 py-1 rounded-full font-semibold">New</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">FoodCompare AI</h3>
                <p className="text-sm sm:text-base text-white/90 mb-5">
                  Swiggy vs Zomato side-by-side. Compare exact dish prices, ratings, and delivery time instantly.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span>Find cheapest meal</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>

            {/* Ride Compare AI Card */}
            <button
              onClick={() => navigate('/ride-compare')}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-purple-600 to-fuchsia-700 p-6 sm:p-8 text-left text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 duration-300"
              data-testid="ride-compare-card"
            >
              <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-40 h-40 bg-cyan-300/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-white/15 px-2 py-1 rounded-full font-semibold">New</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">RideCompare AI</h3>
                <p className="text-sm sm:text-base text-white/90 mb-5">
                  Ola vs Uber. Same route, best fare. Realistic estimates with live surge & ETA.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span>Find cheapest ride</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>

            {/* Grocery Compare AI Card */}
            <button
              onClick={() => navigate('/grocery-compare')}
              className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-600 p-6 sm:p-8 text-left text-white shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 duration-300"
              data-testid="grocery-compare-card"
            >
              <div className="absolute top-0 right-0 -mt-8 -mr-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-40 h-40 bg-lime-300/10 rounded-full blur-3xl" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-white/15 px-2 py-1 rounded-full font-semibold">New</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold mb-2">GroceryCompare AI</h3>
                <p className="text-sm sm:text-base text-white/90 mb-5">
                  Blinkit vs Zepto. Same product, best price. 10-minute grocery delivery comparison.
                </p>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <span>Find cheapest groceries</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-12">
            Everything You Need for Better Health
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <Card key={`item-${idx}`} className="p-6 hover:shadow-lg transition-shadow border-teal-100">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{feature.title}</h3>
                <p className="text-gray-600 text-sm">{feature.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-50 py-8 border-t">
        <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© 2025 HealthAI. All rights reserved. HIPAA Compliant Platform.</p>
        </div>
      </footer>

      <Toaster position="top-right" />
      <AndroidInstallBanner />
    </div>
  );
};

export default LandingPage;
